import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Button, Card, Field, Notice } from '@ailab/ui';
import { messageResponseSchema } from '@ailab/contracts';
import { apiRequest, errorMessage } from '../../lib/http';
import styles from './account.module.css';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const result = await apiRequest('/auth/password/forgot', messageResponseSchema, {
        method: 'POST',
        body: { email },
      });
      setMessage(result.message);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.narrow}>
      <h1>Recuperar la contraseña</h1>
      <p className={styles.lead}>Te enviaremos un enlace que caduca en 1 hora.</p>
      <Card>
        <form className={styles.form} onSubmit={submit} noValidate>
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {message ? <Notice tone="success">{message}</Notice> : null}
          {error ? <Notice tone="error">{error}</Notice> : null}
          <Button type="submit" busy={busy}>
            Enviar enlace
          </Button>
        </form>
        <div className={styles.links}>
          <Link to="/entrar">Volver a entrar</Link>
        </div>
      </Card>
    </div>
  );
}

function tokenFromHash(): string {
  return new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
}

export function ResetPasswordPage() {
  // El token viaja en el fragmento: no llega a los logs del servidor ni al Referer.
  const [token] = useState(tokenFromHash);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError('Las dos contraseñas no coinciden.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const result = await apiRequest('/auth/password/reset', messageResponseSchema, {
        method: 'POST',
        body: { token, password },
      });
      setMessage(result.message);
      window.history.replaceState(null, '', window.location.pathname);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.narrow}>
      <h1>Nueva contraseña</h1>
      <p className={styles.lead}>Al cambiarla se cerrarán todas tus sesiones.</p>
      <Card>
        {!token ? (
          <Notice tone="error">
            El enlace no es válido o ha caducado. <Link to="/recuperar">Pide uno nuevo.</Link>
          </Notice>
        ) : message ? (
          <Notice tone="success">
            {message} <Link to="/entrar">Entrar</Link>
          </Notice>
        ) : (
          <form className={styles.form} onSubmit={submit} noValidate>
            <Field
              label="Nueva contraseña"
              type="password"
              autoComplete="new-password"
              hint="Al menos 10 caracteres."
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Field
              label="Repite la contraseña"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {error ? <Notice tone="error">{error}</Notice> : null}
            <Button type="submit" busy={busy}>
              Guardar contraseña
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
